import {
  ControlPoint as ControlPointIcon,
  EditCalendar as EditCalendarIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { Grid, IconButton, Link, ListItem, ListItemText, Typography } from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { DetailAccordion } from "components/DetailAccordion";
import { AddMachinePropertyDialog } from "components/job/machine/AddMachinePropertyDialog";
import { EditMachineInstallDateDialog } from "components/job/machine/EditMachineInstallDateDialog";
import { EditMachinePropertiesDialog } from "components/job/machine/EditMachinePropertiesDialog";
import { intlFormatDate, isAbortError } from "helpers";
import { formatCustomProperty } from "helpers/formatCustomProperty";
import technicianSettings from "helpers/technicianSettings";
import { PropertyValueInputType, UpdateMachineInputType } from "operations/schema/schema";
import { ChangeJobMachineDialog } from "pages/partner/dialogs/ChangeJobMachineDialog";
import { FC, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import { selectConnectionStatus } from "store/root.store";
import { setOpen } from "store/slices/dialog.store";
import {
  getPartnerJob,
  selectSelectedJob,
  setChangeJobMachineOpen,
  updateMachineCustomProps,
  updateMachineInstallDate,
} from "store/slices/partner.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import { isFlagEnabled } from "store/slices/user.store";

const PREFIX = "JobDetailTab";

const classes = {
  bold: `${PREFIX}-bold`,
  historyIconAdjust: `${PREFIX}-eq-history-icon-adjust`,
  iconAutoMargin: `${PREFIX}-icon-auto-margin`,
  noPadding: `${PREFIX}-no-padding`,
  leftIndent: `${PREFIX}-left-indent`,
  disabled: `${PREFIX}-disabled`,
};

export const MachineDisplay: FC = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const job = useAppSelector(selectSelectedJob);
  const { editMachineLoading } = useAppSelector((s) => s.partner);
  const { machineProperties } = useAppSelector((s) => s.cache);
  const isOnline = useAppSelector(selectConnectionStatus);
  const machineSortGroupsEnabled = useAppSelector((s) => isFlagEnabled(s, "MachineSortGroups"));
  const changeJobMachineEnabled = useAppSelector((s) => isFlagEnabled(s, "ChangeJobMachine"));
  const partnerEditMachineInstallDateEnabled = useAppSelector((s) =>
    isFlagEnabled(s, "PartnerEditMachineInstallDate"),
  );
  const canChangeMachine =
    technicianSettings.isAllowedMobileMachineEdit && isOnline && changeJobMachineEnabled;
  const canChangeInstallDate =
    technicianSettings.isAllowedMobileMachineEdit &&
    technicianSettings.allowEditMachineInstallDate &&
    partnerEditMachineInstallDateEnabled;
  const machineInstallDate = job.machine?.installDate;

  const refetch = useCallback(() => {
    dispatch(getPartnerJob(job.id))
      .then(unwrapResult)
      .catch((e) => {
        if (isAbortError(e)) return;
        dispatch(addSnackbarMessage({ key: "GetJob-fail" }));
      });
  }, [dispatch, job.id]);

  const updateMachinePropertiesCb = useCallback(
    async (values: PropertyValueInputType[]) => {
      const ret = { success: false, queued: false, abort: false };
      await dispatch(updateMachineCustomProps({ properties: values }))
        .then(unwrapResult)
        .then(({ queued }) => {
          ret.success = true;
          ret.queued = Boolean(queued);
        })
        .catch((e) => {
          ret.abort = isAbortError(e);
        });
      return ret;
    },
    [dispatch],
  );

  const updateMachineInstallDateCb = useCallback(
    async (machine: UpdateMachineInputType) => {
      let ret = { success: false, queued: false, abort: false };
      await dispatch(updateMachineInstallDate({ jobId: job.id, machine }))
        .then(unwrapResult)
        .then(({ queued }) => {
          ret.success = true;
          ret.queued = !!queued;
        })
        .catch((e) => {
          ret.abort = isAbortError(e);
        });
      return ret;
    },
    [dispatch, job.id],
  );

  return (
    <>
      <ListItem
        component="div"
        disableGutters
        className={classes.noPadding}
        data-testid="MachineDisplay"
      >
        <ListItemText
          className={classes.bold}
          primary={
            <>
              {job.machine?.name}
              {canChangeMachine && (
                <IconButton
                  aria-label={intl.formatMessage({ id: "visit.changeMachine" })}
                  onClick={() => dispatch(setChangeJobMachineOpen({ open: true }))}
                  data-testid="MachineDisplay-ChangeJobMachine"
                  sx={{
                    fontSize: "1.2rem",
                    paddingY: 0,
                  }}
                >
                  <EditIcon color="primary" />
                </IconButton>
              )}
              {technicianSettings?.isAllowedMobileMachineEdit && (
                <IconButton
                  aria-label={intl.formatMessage({ id: "machine.addProperty" })}
                  onClick={() =>
                    dispatch(setOpen({ dialogName: "addMachineProperty", open: true }))
                  }
                  data-testid="MachineDisplay-OpenAddMachineProperty"
                  sx={{
                    fontSize: "1.2rem",
                    paddingY: 0,
                  }}
                >
                  <ControlPointIcon color="primary" />
                </IconButton>
              )}
            </>
          }
        />
      </ListItem>

      <div className={classes.leftIndent}>
        <Typography>{job.machine?.machineNo}</Typography>
        {job.machine?.idNo && <Typography>{job.machine.idNo}</Typography>}
        {job.machine?.location &&
          (job.machine.location.startsWith("///") ? (
            <Link
              href={`https://what3words.com/${job.machine.location.replace("///", "")}`}
              underline="none"
              color="blue"
              target="_blank"
              rel="noopener noreferrer"
            >
              {job.machine.location}
            </Link>
          ) : (
            <Typography>{job.machine.location}</Typography>
          ))}
        <Typography>
          {machineInstallDate &&
            `${intl.formatMessage({
              id: "general.installed",
            })} ${intlFormatDate(intl, machineInstallDate)}`}
          {!machineInstallDate && <FormattedMessage id="editMachine.addInstallDate" />}
          {canChangeInstallDate && (
            <IconButton
              aria-label={intl.formatMessage({ id: "visit.editMachineInstallDate" })}
              onClick={() =>
                dispatch(setOpen({ dialogName: "editMachineInstallDate", open: true }))
              }
              data-testid="MachineDisplay-ChangeJobMachine"
              sx={{
                fontSize: "1.2rem",
                paddingY: 0,
              }}
            >
              <EditCalendarIcon color="primary" />
            </IconButton>
          )}
        </Typography>
        {machineSortGroupsEnabled && !!job.machine?.details && (
          <DetailAccordion
            data-testid="MachineDisplay-DetailAccordion"
            content={
              <>
                {!!job.machine.details.information && (
                  <Typography>
                    {`${intl.formatMessage({
                      id: "job.machine.details.information",
                    })}: ${job.machine.details.information}`}
                  </Typography>
                )}
                {!!job.machine.details.customProperties?.length && (
                  <Grid container>
                    <Grid item>
                      {job.machine.details.customProperties.map((cp, index) => (
                        <Typography key={`${cp?.code}-${index}`}>
                          {`${cp?.description}: ${formatCustomProperty(intl, cp, machineProperties)}`}
                        </Typography>
                      ))}
                    </Grid>
                    <Grid item xs={2}>
                      {technicianSettings?.isAllowedMobileMachineEdit && (
                        <EditIcon
                          sx={{ ml: 3 }}
                          onClick={() =>
                            dispatch(setOpen({ dialogName: "editMachineProperties", open: true }))
                          }
                          data-testid="MachineDisplay-OpenEditMachine"
                        />
                      )}
                    </Grid>
                  </Grid>
                )}
                {!!job.machine.details.sortGroups?.length &&
                  job.machine.details.sortGroups.map((sg, index) => (
                    <Typography key={`sortgroup-${index}`}>
                      {`${sg?.description}: ${sg?.value}`}
                    </Typography>
                  ))}
              </>
            }
          />
        )}
      </div>
      {canChangeMachine && <ChangeJobMachineDialog handleFinished={refetch} />}
      {job.machine && technicianSettings?.isAllowedMobileMachineEdit && (
        <>
          <EditMachinePropertiesDialog
            loading={editMachineLoading}
            updateMachineProperties={updateMachinePropertiesCb}
          />
          <AddMachinePropertyDialog
            loading={editMachineLoading}
            updateMachineProperties={updateMachinePropertiesCb}
          />
        </>
      )}
      {job.machine && canChangeInstallDate && (
        <EditMachineInstallDateDialog
          machine={job.machine}
          externalId={job.externalId}
          loading={editMachineLoading}
          updateMachine={updateMachineInstallDateCb}
        />
      )}
    </>
  );
};
